<template>
  <kpi-tracking-details
    v-if="!!firstRowData || !!secondRowData"
    :title="sectionTitle"
    :headers="headers"
    :first-row="{
      title: firstRowTitle,
      data: firstRowData
    }"
    :second-row="{
      title: secondRowTitle,
      data: secondRowData
    }"
    :first-row-ytd="firstRowYtd"
    :second-row-ytd="secondRowYtd"
    @see-detail="handleSeeDetails"
  >
    <template #first-ytd-chart>
      <LineChart
        v-if="firstRowYTDAvailable"
        :data="generateLineData(
          firstRowYtd.color,
          firstRowData.current_year_running_performance_actual,
          firstRowData.current_year_running_performance_plan
        )"
        :options="lineChartOptions"
        :plugins="Array(chartPlugins.line.find(({ id }) => id === 'customFill'))"
        class="h-32"
      />
      <div
        v-else
        class="flex items-center justify-center h-full"
      >
        <span class="text-grey-001">No Data Found</span>
      </div>
    </template>
    <template #first-mtd-chart>
      <LineChart
        v-if="firstRowMTDAvailable"
        :data="generateLineData(
          firstRowYtd.color,
          firstRowData.current_month_running_performance_actual,
          firstRowData.current_month_running_performance_plan
        )"
        :options="lineChartOptions"
        :plugins="Array(chartPlugins.line.find(({ id }) => id === 'customCanvasBackgroundColor'))"
        class="h-32"
      />
      <div
        v-else
        class="flex items-center justify-center h-full"
      >
        <span class="text-grey-001">No Data Found</span>
      </div>
    </template>
    <template #second-ytd-chart>
      <LineChart
        v-if="secondRowYTDAvailable"
        :data="generateLineData(
          secondRowYtd.color,
          secondRowData.current_year_running_performance_actual,
          secondRowData.current_year_running_performance_plan,
          false
        )"
        :options="lineChartOptions"
        :plugins="Array(chartPlugins.line.find(({ id }) => id === 'customFill'))"
        class="h-32"
      />
      <div
        v-else
        class="flex items-center justify-center h-full"
      >
        <span class="text-grey-001">No Data Found</span>
      </div>
    </template>
    <template #second-mtd-chart>
      <LineChart
        v-if="secondRowMTDAvailable"
        :data="generateLineData(
          secondRowYtd.color,
          secondRowData.current_month_running_performance_actual,
          secondRowData.current_month_running_performance_plan,
          false
        )"
        :options="lineChartOptions"
        :plugins="Array(chartPlugins.line.find(({ id }) => id === 'customCanvasBackgroundColor'))"
        class="h-32"
      />
      <div
        v-else
        class="flex items-center justify-center h-full"
      >
        <span class="text-grey-001">No Data Found</span>
      </div>
    </template>
    <template
      v-if="hasAveragePieChart"
      #average-pie-chart
    >
      <div
        class="w-1/4 h-full p-3 shadow-md rounded-lg flex flex-col gap-5"
      >
        <div class="self-end flex flex-row gap-2">
          <date-picker
            class="date-picker-filter"
            type="month"
            value-type="MM"
            format="MMM"
            :clearable="false"
            v-model="pieChartFilter.month"
          />
          <date-picker
            class="date-picker-filter"
            type="year"
            value-type="YYYY"
            format="YYYY"
            :clearable="false"
            v-model="pieChartFilter.year"
          />
        </div>
        <div class="w-full h-full flex flex-col gap-5">
          <span class="text-md font-semibold">
            {{ averagePieChartTitle }}
          </span>
          <div
            v-if="averageDetailsAvailable"
            class="flex-1 flex flex-col justify-around gap-5"
          >
            <doughnut-chart
              :data="generateDoughnutData()"
              :options="doughnutChartOptions"
              :plugins="chartPlugins.doughnut"
              class="h-60"
            />
            <div
              :id="`legend-container-${type}`"
            />
          </div>
          <div
            v-else
            class="flex items-center justify-center h-full"
          >
            <span class="text-grey-001">No Data Found</span>
          </div>
        </div>
      </div>
    </template>
    <template #rank-bar-chart>
      <div
        :class="hasAveragePieChart ? 'ml-5 w-3/4' : 'w-full'"
        class="p-3 h-full flex flex-col gap-5 shadow-md rounded-lg"
      >
        <div class="self-end flex flex-row gap-2">
          <date-picker
            class="date-picker-filter"
            type="month"
            value-type="MM"
            format="MMM"
            :clearable="false"
            v-model="barChartFilter.month"
          />
          <date-picker
            class="date-picker-filter"
            type="year"
            value-type="YYYY"
            format="YYYY"
            :clearable="false"
            v-model="barChartFilter.year"
          />
        </div>
        <div class="w-full h-full flex flex-row gap-5">
          <div
            v-for="(chartdata, index) in rankData"
            :key="index"
            class="flex flex-col"
            :class="`w-1/${rankData.length}`"
          >
            <span
              v-show="!hideChart.includes(chartdata.title)"
              class="font-semibold"
            >
              Ranking: {{ chartdata.title }}
            </span>
            <bar-chart
              v-show="!hideChart.includes(chartdata.title)"
              v-if="!chartdata.isEmpty"
              :key="`chart-${chartdata.title}-${chartdata.config.datasets[0].data.length}`"
              :data="chartdata.config"
              :options="barChartOptions"
              :plugins="chartPlugins.bar"
              :width="250"
              :height="chartdata.height"
            />
            <span
              v-else
              v-show="!hideChart.includes(chartdata.title)"
              class="mt-5 text-grey-001"
            >
              No Data Found
            </span>
          </div>
        </div>
        <div class="flex flex-row gap-1 w-fit self-end font-semibold">
          <span>View more details in</span>
          <span
            class="cursor-pointer underline"
            style="color: #004479"
            @click="$router.push(redirectTo.page)"
          >
            {{ redirectTo.pageTitle }}
          </span>
        </div>
      </div>
    </template>
  </kpi-tracking-details>
</template>

<script>
import {isEmpty, sumBy} from "lodash";
import moment from "moment/moment";

import BarChart from "@/components/barChart";
import DatePicker from '@/components/datePicker'
import DoughnutChart from "@/components/doughnutChart";
import LineChart from "@/components/lineChart";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";
import KpiTrackingDetails from "@/views/kpiTracking/components/KpiTrackingDetails.vue";

export default {
  name: 'KpiCard',
  components: {
    KpiTrackingDetails,
    LineChart,
    DoughnutChart,
    BarChart,
    DatePicker
  },
  props: {
    type: {
      type: String,
      default: 'barge_route'
    },
    data: {
      type: Object,
      default: () => {}
    },
    chartOptions: {
      type: Object,
      default: () => {}
    },
    chartPlugins: {
      type: Object,
      default: () => {}
    },
    hasAveragePieChart: {
      type: Boolean,
      default: true
    },
    averagePieChartTitle: {
      type: String,
      default: ''
    }
  },
  computed: {
    headers() {
      const currentMonth = this.moment().month()
      const monthRunningPerformance = []
      for (let i = 0; i <= currentMonth; i++) {
        if (i < currentMonth) {
          monthRunningPerformance.push(this.moment().month(i).format('MMM'))
        } else {
          monthRunningPerformance.push("MTD")
        }
      }

      const startOfMonth = this.moment().startOf('month')
      const endOfMonth = this.moment().endOf('month')
      const diff = endOfMonth.diff(startOfMonth)
      const diffInWeeks = Math.round(moment.duration(diff).asWeeks())
      const weekRunningPerformance = []
      for (let i = 0; i < diffInWeeks; i++) {
        weekRunningPerformance.push(`W${i+1}`)
      }

      return {
        a: `${this.previousYear}A`,
        b: `${this.currentYear}B`,
        ytd: `${this.currentYear}YTD`,
        ytdPerformance: {
          key: 'ytd',
          title: `${this.currentYear} Running Performance`,
          value: monthRunningPerformance
        },
        mtdPerformance: {
          key: 'mtd',
          title: `MTD Running Performance (${this.moment().format('MMM')})`,
          value: weekRunningPerformance
        }
      }
    },
    sectionTitle() {
      switch (this.type) {
        case (this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE):
          return 'Barge Route Optimize (USD/Ton)'
        case (this.KPI_TYPE.BARGE_MONITORING):
          return 'Barge Monitoring (Days)'
        case (this.KPI_TYPE.FUEL_OPTIMIZATION):
          return 'Fuel Optimization'
        case (this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION):
          return 'Loading Capacity Monitoring (%)'
        default:
          return 'Section Title'
      }
    },
    firstRowTitle() {
      switch (this.type) {
        case (this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE):
          return 'Barge Cost (USD/Ton)'
        case (this.KPI_TYPE.BARGE_MONITORING):
          return 'Cycle Time'
        case (this.KPI_TYPE.FUEL_OPTIMIZATION):
          return 'Fuel Consumption (L/Ton)'
        case (this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION):
          return '% Cargo Capacity Utilization'
        default:
          return 'First Row Title'
      }
    },
    secondRowTitle() {
      switch (this.type) {
        case (this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE):
          return '% Spot Charter / Total Charter'
        case (this.KPI_TYPE.BARGE_MONITORING):
          return 'Actual Barge Usage'
        case (this.KPI_TYPE.FUEL_OPTIMIZATION):
          return 'Fuel Usage vs Plan (KL)'
        case (this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION):
          return '% Schedule Volume for the week VS Actual Volume'
        default:
          return 'Second Row Title'
      }
    },
    firstRowYtd() {
      if (this.firstRowData) {
        if (this.type === this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE) {
          return this.firstRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        } else if (this.type === this.KPI_TYPE.FUEL_OPTIMIZATION) {
          return this.firstRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        } else if (this.type === this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION) {
          const isIconDown = Math.round(this.firstRowData.current_year_percentage) < 0
          const isNoIcon = Math.round(this.firstRowData.current_year_percentage) === 0
          return Math.round(this.firstRowData.current_year_percentage) !== 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: isIconDown ? 'trending_down'  : isNoIcon ? '' : 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: isIconDown ? 'trending_down'  : isNoIcon ? '' : 'trending_up'
            }
        } else if (this.type === this.KPI_TYPE.BARGE_MONITORING) {
          return this.firstRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        } else {
          return this.firstRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        }
      }
      return null
    },
    secondRowYtd() {
      if (this.secondRowData) {
        if (this.type === this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE) {
          return this.secondRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        } else if (this.type === this.KPI_TYPE.FUEL_OPTIMIZATION) {
          return this.secondRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        } else if (this.type === this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION) {
          const isIconDown = Math.round(this.firstRowData.current_year_percentage) < 0
          const isNoIcon = Math.round(this.firstRowData.current_year_percentage) === 0
          return Math.round(this.firstRowData.current_year_percentage) !== 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: isIconDown ? 'trending_down'  : isNoIcon ? '' : 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: isIconDown ? 'trending_down'  : isNoIcon ? '' : 'trending_up'
            }
        } else if (this.type === this.KPI_TYPE.BARGE_MONITORING) {
          return this.secondRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        } else {
          return this.secondRowData.current_year_percentage >= 0
            ? {
              class: 'red',
              color: '#FF3662',
              icon: 'trending_up'
            }
            : {
              class: 'green',
              color: '#33B57D',
              icon: 'trending_down'
            }
        }
      }
      return null
    },
    firstRowYTDAvailable() {
      return !!this.firstRowData?.current_year_running_performance_actual && this.firstRowData?.current_year_running_performance_actual?.length !== 0
    },
    firstRowMTDAvailable() {
      return !!this.firstRowData?.current_month_running_performance_actual && this.firstRowData?.current_month_running_performance_actual?.length !== 0
    },
    secondRowYTDAvailable() {
      return !!this.secondRowData?.current_year_running_performance_actual && this.secondRowData?.current_year_running_performance_actual?.length !== 0
    },
    secondRowMTDAvailable() {
      return !!this.secondRowData?.current_month_running_performance_actual && this.secondRowData?.current_month_running_performance_actual?.length !== 0
    },
    averageDetailsAvailable() {
      const averageKey = Object.keys(this.details).find((key) => key.includes('avg'))
      const avgArr = this.details?.[averageKey]
      return Array.isArray(avgArr) && avgArr.every((cost) => cost.value !== null  && cost.value !== undefined)
    },
    redirectTo() {
      const redirectTo = {
        pageTitle: null,
        page: { name: 'Login', replace: true }
      }
      switch (this.type) {
        case this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE:
          redirectTo.pageTitle = 'Operation Cost Utilization'
          redirectTo.page.name = 'CycleTimeUtilization'
          break
        case this.KPI_TYPE.BARGE_MONITORING:
          redirectTo.pageTitle = 'Operation Time Utilization'
          redirectTo.page.name = 'CycleTimeUtilization'
          break
        case this.KPI_TYPE.FUEL_OPTIMIZATION:
          redirectTo.pageTitle = 'Fuel Supply Monitoring'
          redirectTo.page.name = 'FuelSupplyManagement'
          break
        case this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION:
          redirectTo.pageTitle = 'Cargo Capacity Utilization'
          redirectTo.page.name = 'CargoCapacityUtilization'
          break
      }
      return redirectTo
    },
    lineChartOptions() {
      if (!this.chartOptions) return null
      return {
        ...this.chartOptions.line,
        plugins: {
          ...this.chartOptions.line?.plugins,
          tooltip: {
            enabled: true,
            backgroundColor: '#FAFDFF',
            bodyColor: '#000000',
            font: { family: 'Poppins' },
            borderWidth: 1,
            padding: 10,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              label(tooltipItem) {
                const value = handleValidateAndFormatNumber(parseFloat(tooltipItem?.raw), 2)
                return `${value} ${tooltipItem?.dataset?.tooltipUnit}`
              }
            }
          }
        }
      }
    },
    doughnutChartOptions() {
      if (!this.chartOptions) return null
      let tooltips = []
      if (this.type === this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE) {
        tooltips = this.details?.avgOperationCost?.map((item) => ({
          label: item.label,
          firstRow: `Cost: ${ handleValidateAndFormatNumber(item?.cost) } USD`,
          secondRow: `Cost: ${ handleValidateAndFormatNumber(item.cost_per_mt, 3) } USD/Ton`
        })) || []
      } else if (this.type === this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION) {
        tooltips = this.details?.avgCargoLoadingTarget?.map((item) => ({
          label: item.label,
          firstRow: `${ handleValidateAndFormatNumber(item?.trip) } Trips`,
          secondRow: `${ handleValidateAndFormatNumber(item.value) }%`
        })) || []
      } else if (this.type === this.KPI_TYPE.BARGE_MONITORING) {
        const avgCycleDays = sumBy(this.details?.avg_operation_time,'value')
        tooltips = this.details?.avg_operation_time?.map((item) => ({
          label: item.label,
          firstRow: `${ handleValidateAndFormatNumber(item?.days) } Days`,
          secondRow: `${ handleValidateAndFormatNumber(item?.value) } %`
        })) || []
      }
      const tooltipMap = new Map(tooltips.map(item => [item.label, item]));
      return {
        ...this.chartOptions.doughnut,
        plugins: {
          ...this.chartOptions.doughnut?.plugins,
          htmlLegend: {
            containerID: `legend-container-${this.type}`
          },
          tooltip: {
            enabled: true,
            backgroundColor: '#FAFDFF',
            bodyColor: '#000000',
            font: { family: 'Poppins' },
            borderWidth: 1,
            padding: 10,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              label(tooltipItem) {
                const tooltip = tooltipMap.get(tooltipItem.label)
                if (tooltip) {
                  return [tooltip.firstRow, tooltip.secondRow]
                }
              }
            }
          }
        }
      }
    },
    barChartOptions() {
      if (!this.chartOptions) return null
      let tooltips = []
      if (this.type === this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE) {
        tooltips = this.details?.rank?.map((item) => ({
          label: item.label,
          values: item.data.map((data) => ({
            label: data.owner_name,
            value: `${data.value} USD/ton`
          }))
        })) || []
      } else if (this.type === this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION) {
        tooltips = this.details?.avgCargoLoadingTarget?.map((item) => ({
          label: item.label,
          firstRow: `${ handleValidateAndFormatNumber(item?.trip) } Trips`,
          secondRow: `${ handleValidateAndFormatNumber(item.value) }%`
        })) || []
      }
      const tooltipMap = new Map(tooltips.map(item => [item.label, item]));
      return {
        ...this.chartOptions.bar,
        barThickness: 25,
        plugins: {
          ...this.chartOptions.bar?.plugins,
          tooltip: {
            enabled: true,
            backgroundColor: '#FAFDFF',
            bodyColor: '#000000',
            font: { family: 'Poppins' },
            borderWidth: 1,
            padding: 10,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title() {},
              label(tooltipItem) {
                const value = handleValidateAndFormatNumber(tooltipItem?.raw?.value)
                return `${value} ${tooltipItem?.dataset?.tooltipUnit}`
              }
            }
          }
        }
      }
    }
  },
  watch: {
    data: {
      deep: true,
      handler(val) {
        if (val) {
          const firstKey = Object.keys(this.data).find(key => this.data[key]?.sequence_order === 1)
          const secondKey = Object.keys(this.data).find(key => this.data[key]?.sequence_order === 2)
          const thirdKey = Object.keys(this.data).find(key => this.data[key]?.sequence_order === 3)

          // Map barge monitoring data to expected structure
          if (this.type === this.KPI_TYPE.BARGE_MONITORING) {
            const rawFirst = this.data[firstKey] || {}
            const rawSecond = this.data[secondKey] || {}

            const generateRange = (length, start = 0, formatter = i => i + 1) =>
              Array.from({ length }, (_, i) => formatter(i + start))

            const generateMapData = (data = [], key = 'month', valueKey = 'value') =>
              new Map(data.map(item => [item?.[key], item?.[valueKey]]))

            const mapDataToRange = (range, dataMap) =>
              range.map(val => dataMap.get(parseInt(val)) || 0)

            const currentMonth = this.moment().month()
            const monthRunningPerformance = generateRange(currentMonth + 1, 0, i => this.moment().month(i).format('M'))

            const diffInWeeks = Math.round(
              this.moment().endOf('month').diff(this.moment().startOf('month'), 'weeks', true)
            )
            const weekRunningPerformance = generateRange(diffInWeeks)

            const firstCurrYearPlan = mapDataToRange(
              monthRunningPerformance,
              generateMapData(rawFirst?.current_monthly_running_performance_plan)
            )

            const firstCurrYearActual = mapDataToRange(
              monthRunningPerformance,
              generateMapData(rawFirst?.current_monthly_running_performance_actual)
            )

            const firstCurrMonthPlan = mapDataToRange(
              weekRunningPerformance,
              generateMapData(rawFirst?.current_weekly_running_performance_plan, 'week')
            )

            const firstCurrMonthActual = mapDataToRange(
              weekRunningPerformance,
              generateMapData(rawFirst?.current_weekly_running_performance_actual, 'week')
            )

            this.firstRowData = {
              previous_year: rawFirst.previous_year_actual ?? 0,
              current_year: rawFirst.current_year_plan ?? 0,
              current_year_actual: rawFirst.current_year_actual ?? 0,
              current_year_percentage: rawFirst.current_year_ytd_percentage ?? 0,
              current_year_running_performance_actual: firstCurrYearActual,
              current_year_running_performance_plan: firstCurrYearPlan,
              current_month_running_performance_actual: firstCurrMonthActual,
              current_month_running_performance_plan: firstCurrMonthPlan
            }

            const secondCurrYearPlan = mapDataToRange(
              monthRunningPerformance,
              generateMapData(rawSecond?.current_monthly_running_performance_plan)
            )

            const secondCurrYearActual = mapDataToRange(
              monthRunningPerformance,
              generateMapData(rawSecond?.current_monthly_running_performance_actual)
            )

            const secondCurrMonthPlan = mapDataToRange(
              weekRunningPerformance,
              generateMapData(rawSecond?.current_weekly_running_performance_plan, 'week')
            )

            const secondCurrMonthActual = mapDataToRange(
              weekRunningPerformance,
              generateMapData(rawSecond?.current_weekly_running_performance_actual, 'week')
            )

            this.secondRowData = {
              previous_year: rawSecond.previous_year_actual ?? 0,
              current_year: rawSecond.current_year_plan ?? 0,
              current_year_actual: rawSecond.current_year_actual ?? 0,
              current_year_percentage: rawSecond.current_year_ytd_percentage ?? 0,
              current_year_running_performance_actual: secondCurrYearActual,
              current_year_running_performance_plan: secondCurrYearPlan,
              current_month_running_performance_actual: secondCurrMonthActual,
              current_month_running_performance_plan: secondCurrMonthPlan
            }
          } else {
            this.firstRowData = this.data[firstKey]
            this.secondRowData = this.data[secondKey]
          }

          this.details = this.data[thirdKey]
          this.rankData = this.processRankData(this.data[thirdKey]?.rank)

          // Normalize avg data for doughnut chart
          if (this.type === this.KPI_TYPE.BARGE_MONITORING && this.details && typeof this.details.avg_operation_time === 'object' && Array.isArray(this.details.avg_operation_time.data)) {
            this.details.avg_operation_time = this.details.avg_operation_time.data;
          }
        }
      }
    },
    pieChartFilter: {
      deep: true,
      handler(val) {
        const month = !!val.month ? val.month : this.currentMonth
        const year = !!val.year ? val.year : this.currentYear
        this.$emit('update:pie-filter', `${month}-${year}`)
      }
    },
    barChartFilter: {
      deep: true,
      handler(val) {
        const month = !!val.month ? val.month : this.currentMonth
        const year = !!val.year ? val.year : this.currentYear
        this.$emit('update:rank-filter', `${month}-${year}`)
      }
    }
  },
  data() {
    const currentYear = new Date().getFullYear()
    const currentMonth =  this.moment().format('MM')
    return {
      previousYear: new Date().getFullYear() - 1,
      currentYear: currentYear,
      currentMonth: currentMonth,
      isExpanded: false,
      pieChartFilter: {
        month: currentMonth,
        year: currentYear.toString()
      },
      barChartFilter : {
        month: currentMonth,
        year: currentYear.toString()
      },
      firstRowData: null,
      secondRowData: null,
      details: null,
      rankData: [],
      KPI_TYPE: {
        BARGE_ROUTE_OPTIMIZE: 'barge_route_optimize',
        BARGE_MONITORING: 'barge_monitoring',
        FUEL_OPTIMIZATION: 'fuel_optimization',
        LOADING_CAPACITY_UTILIZATION: 'loading_capacity_utilization'
      },
      hideChart: []
    }
  },
  methods: {
    generateLineData(color, data, baseline = null, isFirstRow = true) {
      // Chart.js datasets should be numbers (not localized strings like "32,000").
      // Keep formatting for tooltips only (see `lineChartOptions()`).
      const numericData = (Array.isArray(data) ? data : []).map((value) => {
        const n = parseFloat(value)
        return Number.isFinite(n) ? n : 0
      })

      if (isEmpty(baseline) || !Array.isArray(baseline)) {
        const sum = numericData.reduce((a, b) => a + b, 0)
        const avg = numericData.length ? (sum / numericData.length) + 0.25 : 0
        baseline = Array(numericData.length).fill(avg)
      }

      const units = new Map()
      units.set(this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE, {
        firstRow: 'USD/ton',
        secondRow: '%'
      })
      units.set(this.KPI_TYPE.BARGE_MONITORING, {
        firstRow: 'days',
        secondRow: 'Barge'
      })
      units.set(this.KPI_TYPE.FUEL_OPTIMIZATION, {
        firstRow: 'L/Ton',
        secondRow: 'kL'
      })
      units.set(this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION, {
        firstRow: '%',
        secondRow: 'USD/ton'
      })

      let unit = units.get(this.type)
      if (typeof unit === 'object') {
        unit = isFirstRow ? unit.firstRow : unit.secondRow
      }

      const options = {
        labels: Array(data.length).fill(''),
        datasets: [
          {
            data: numericData,
            fill: false,
            borderWidth: 2,
            borderColor: color,
            borderCapStyle: 'round',
            pointRadius: 4,
            pointBackgroundColor: color,
            tooltipUnit: unit
          },
          {
            ...(!!baseline && {
              data: baseline,
              borderWidth: 1,
              borderColor: '#7889DA',
              borderDash: [3, 3],
              pointRadius: 4,
              pointHoverRadius: 4,
              pointHitRadius: 12,
              isFirstRow,
              tooltipUnit: unit
            })
          }
        ]
      }
      return options
    },
    generateDoughnutData() {
      const averageKey = Object.keys(this.details).find((key) => key.includes('avg'))
      const data = this.details?.[averageKey]
      const colors = this.defineColor(data)
      data.forEach((item) => {
        item['color'] = colors.find(({label}) => label === item.label).color
      })
      return {
        labels: data.map(({ label }) => label),
        datasets: [
          {
            data: data.map(({ value }) => value),
            backgroundColor: data.map(({ color }) => color)
          }
        ]
      }
    },
    handleSeeDetails() {
      this.isExpanded = !this.isExpanded
    },
    defineColor(data) {
      const colors = [
        {
          labels: [
            // Barge Route Optimization
            'Fuel Cost',

            // Barge Monitoring
            'Cycle Time',
            'Total Operation Time',

            //Loading Capacity Utilization
            'Total Poor'
          ],
          color: '#7239EA'
        },
        {
          labels: [
            // Barge Route Optimization
            'Spot Cost',

            // Barge Monitoring
            'Delay From Owner',
            'Total Delay Time From Owner',

            //Loading Capacity Utilization
            'Total Achieved',
            'Cargo Capacity(Routes)'
          ],
          color: '#50CD89'
        },
        {
          labels: [
            // Barge Route Optimization
            'Time Cost',

            // Fuel Optimization
            'Fuel Consumption',

            // Barge Monitoring
            'Delay From Root Cause',
            'Total Delay Time From Charterer',

            //Loading Capacity Utilization
            'Total Exceed',
            'Cargo Capacity(POL)'
          ],
          color: '#3E97FF'
        },
        {
          labels: [
            // Barge Monitoring
            'Total Missing Time'
          ],
          color: '#FF3662'
        }
      ]
      return data.map(item => {
        const match = colors.find(({ labels }) =>
          labels.some(label => item.label.includes(label))
        )
        return {
          ...item,
          color: match ? match.color : '#999'
        }
      })
    },
    processRankData(rawData) {
      // Normalize rawData to always be an array
      let normalizedData = []
      if (Array.isArray(rawData)) {
        normalizedData = rawData
      } else if (rawData && Array.isArray(rawData.data)) {
        normalizedData = rawData.data
      } else {
        return []
      }

      if (Array.isArray(normalizedData) && normalizedData.every(({ data }) => data?.length === 0)) {
        return Array(normalizedData.length).fill({
          isEmpty: true
        })
      }

      const processedData = normalizedData.map(item => {
        const key = item.data && item.data[0] ? Object.keys(item.data[0]).find(key => key !== 'value') : null
        return {
          ...item,
          data: item.data
            // Keep points where value is 0; only drop null/undefined.
            ? item.data.filter(entry => entry?.value !== null && entry?.value !== undefined)
              .map(entry => ({
                ...entry,
                key: key ? entry[key] : undefined
              }))
            : []
        }
      })

      const defineColor = this.defineColor(processedData)

      const labeledData = Object.fromEntries(
        processedData.map(item => [
          item.label.toLowerCase().replace(' ', '_'),
          item
        ])
      );

      const baseHeight = this.type === this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION
        ? 40
        : 50
      const barThickness = this.type === this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION
        ? 20
        : 35

      const units = new Map()
      units.set(this.KPI_TYPE.BARGE_ROUTE_OPTIMIZE, 'USD/ton')
      units.set(this.KPI_TYPE.BARGE_MONITORING, 'days')
      units.set(this.KPI_TYPE.FUEL_OPTIMIZATION, 'L/Ton')
      units.set(this.KPI_TYPE.LOADING_CAPACITY_UTILIZATION, '%')

      const unit = units.get(this.type)

      return Object.values(labeledData).map((item, index) => {
        const padding = item.data.length > 5 ? 25 : -15
        return {
          title: item.label,
          config: {
            labels: item.data.map(d => d.key),
            datasets: [
              {
                label: item.label,
                data: item.data,
                borderWidth: 1,
                borderRadius: 5,
                backgroundColor: defineColor.find(c => c.label === item.label)?.color,
                tooltipUnit: Array.isArray(unit) ? unit[index] : unit
              }
            ]
          },
          isEmpty: item.data.length === 0,
          height: Math.min(400, baseHeight + (item.data.length * barThickness) + padding)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.date-picker-filter {
  @apply h-10 w-32 self-end border border-primary rounded-lg;

  /deep/.mx-input-wrapper {
    @apply px-3 py-2;
    color: #6546C3;
    background: transparent !important;

    .mx-input {
      @apply text-primary;
    }
  }
}
</style>
